import React, { useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Navigate } from "react-router-dom";
import SignInPage from "./components/SignInPage";
import SignUpPage from "./components/SignUpPage";
import Homepage from "./pages/HomePage"; 
import { ErrorPage } from "./pages/ErrorPage";
import StoryPlayerPage from "./pages/StoryPlayerPage";
import PublicStoryPlayerPage from "./pages/PublicStoryPlayerPage";
import StyledAlertHost from "./components/StyledAlert";
import AppAuthGate from './components/AppAuthGate';
import BrandPalettePreviewPage from './pages/BrandPalettePreviewPage';
import UserGuidePage from './pages/UserGuidePage';

const PUBLIC_SITE_URL = 'https://www.marstrix.com';

function ExternalRedirect({ path }) {
  useEffect(() => {
    const returnTo = encodeURIComponent(`${window.location.origin}/protected`);
    window.location.replace(`${PUBLIC_SITE_URL}${path}?returnTo=${returnTo}`);
  }, [path]);

  return null;
}

function SignInRoute() {
  return (
    <>
      <SignedIn><Navigate to="/protected" replace /></SignedIn>
      <SignedOut><SignInPage /></SignedOut>
    </>
  );
}

function SignUpRoute() {
  return (
    <>
      <SignedIn><Navigate to="/protected" replace /></SignedIn>
      <SignedOut><SignUpPage /></SignedOut>
    </>
  );
}

function App() {
  const theme = createTheme({
    palette: {
      mode: 'light',
      primary: { main: '#3A6752', dark: '#274D3B', light: '#6F9654', contrastText: '#FFFFFF' },
      secondary: { main: '#6F9654', dark: '#3A6752', light: '#E6EED1', contrastText: '#1F352A' },
      info: { main: '#6F9654', dark: '#274D3B', light: '#E6EED1', contrastText: '#FFFFFF' },
      background: { default: '#FBF8EF', paper: '#FFFFFF' },
      text: { primary: '#1F352A', secondary: '#647269' },
      divider: '#D6DED2',
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          containedPrimary: { backgroundColor: '#274D3B', '&:hover': { backgroundColor: '#1F3D30' } },
          outlinedPrimary: { borderColor: '#274D3B', color: '#274D3B', '&:hover': { borderColor: '#1F3D30', backgroundColor: '#EDF1DF' } },
          textPrimary: { color: '#274D3B', '&:hover': { backgroundColor: '#EDF1DF' } },
        },
      },
      MuiIconButton: { styleOverrides: { colorPrimary: { color: '#3A6752' } } },
      MuiLink: { styleOverrides: { root: { color: '#3A6752' } } },
      MuiCard: { styleOverrides: { root: { borderColor: '#D6DED2' } } },
      MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    },
  });
    return (
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <StyledAlertHost />
          <Routes>
            <Route path="/" element={
              <>
                <SignedIn><Navigate to="/protected" /></SignedIn>
                <SignedOut><Navigate to="/sign-in" /></SignedOut>
              </>
            } />
            <Route path="/protected" element={<AppAuthGate><Homepage /></AppAuthGate>} />
            <Route path="/error" element={<ErrorPage />} />
            {/* need to set redirect url in clerk also: Use afterSignInUrl and afterSignUpUrl to redirect to different routes */}
            <Route path="/sign-in/*" element={<SignInRoute />} />
            <Route path="/sign-up/*" element={<SignUpRoute />} />
            {/* <Route path="/sign-in/*" element={<SignIn routing="path" path="/sign-in" afterSignInUrl="/after-sign-in" />} />*/}
            {/* <Route path="/sign-up/*" element={<SignUp routing="path" path="/sign-up" afterSignUpUrl="/after-sign-up" />} /> */}
            {/* Legacy Clerk redirect paths converge on the single AppAuthGate exchange. */}
            <Route path="/after-sign-in" element={<Navigate to="/protected" replace />} />
            <Route path="/after-sign-up" element={<Navigate to="/protected" replace />} />
            {/* Add other routes here */}
            <Route path="/story-player" element={<StoryPlayerPage />} />
            {/* Public shared story route (no auth required) */}
            <Route path="/public/story/:shareSlug" element={<PublicStoryPlayerPage />} />
            <Route path="/terms" element={<ExternalRedirect path="/terms" />} />
            <Route path="/privacy" element={<ExternalRedirect path="/privacy" />} />
            <Route path="/design-palette" element={<BrandPalettePreviewPage />} />
            <Route path="/user-guide" element={<UserGuidePage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
  );
}

export default App;
